export function numberWithCommas(x: number) {
  if (typeof x === "undefined" || isNaN(x)) return;
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function convertToKobo(amount: number) {
  return amount * 100;
}

export function divideKobo(amount: number) {
  return amount / 100;
}

export const objectKeyToWord = (key: string) => {
  const res: string[] = [];
  key
    .split("_")
    .forEach((i) => res.push(`${i[0].toUpperCase()}${i.slice(1, i.length)}`));
  return res.join(" ");
};

export const paystackChargeCalculator = (amount: number) => {
  if (!amount) return 0;
  if (amount < 5000) {
    return 10;
  } else if (amount < 50000) {
    return 25;
  } else {
    return 50;
  }
};

export const formatMoney = (n: number) => {
  const res = new Intl.NumberFormat().format(n);
  return res;
};

export const sentenceCase = (i: string) => {
  return i.toLowerCase().charAt(0).toUpperCase() + i.slice(1);
};

export const asyncForEach = async <T,>(
  array: T[],
  callback: (item: T, index: number, array: T[]) => Promise<void>
): Promise<void> => {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array);
  }
};

export const getFullName = (
  firstName: string | null = "",
  lastName: string | null = "",
  emptyState?: string
) => {
  if (firstName && lastName) return `${firstName} ${lastName}`;
  else if (firstName) return firstName;
  else if (lastName) return lastName;
  return emptyState ?? "-";
};

export const getFileDimension = (
  file: File
): Promise<{ height: number; width: number }> => {
  return new Promise((resolve, reject) => {
    const fileType = file.type.split("/")[0]; // 'image' or 'video'

    if (fileType === "image") {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const image = new Image();
        image.src = reader.result as string;
        image.onload = () => {
          const { height, width } = image;
          resolve({ height, width });
        };
        image.onerror = () => reject(new Error("Failed to load image"));
      };
      reader.onerror = () => reject(new Error("Failed to read image file"));
    } else if (fileType === "video") {
      const video = document.createElement("video");
      const url = URL.createObjectURL(file);
      video.src = url;
      video.onloadedmetadata = () => {
        const { videoHeight: height, videoWidth: width } = video;
        URL.revokeObjectURL(url); // Clean up the object URL
        resolve({ height, width });
      };
      video.onerror = () => {
        URL.revokeObjectURL(url); // Clean up on error
        reject(new Error("Failed to load video metadata"));
      };
    } else {
      reject(new Error("Unsupported file type"));
    }
  });
};

export const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    // Create a temporary video element
    const video = document.createElement("video");

    // Create a URL for the file
    const objectUrl = URL.createObjectURL(file);
    video.src = objectUrl;

    // When metadata is loaded, resolve with duration
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(objectUrl); // Clean up
      resolve(video.duration); // Duration in seconds
    };

    // Handle errors
    video.onerror = () => {
      URL.revokeObjectURL(objectUrl); // Clean up
      reject(new Error("Failed to load video metadata"));
    };
  });
};

export const filterShortVideos = async (
  filesArray: File[]
): Promise<File[]> => {
  const result: File[] = [];

  for (const file of filesArray) {
    if (!file.type.includes("video")) continue;

    try {
      const duration = await getVideoDuration(file);
      if (duration < 10) {
        result.push(file);
      }
    } catch (err) {
      console.warn(`Failed to get duration for file ${file.name}`, err);
    }
  }

  return result;
};

export const filterLongVideos = async (filesArray: File[]): Promise<File[]> => {
  const result: File[] = [];

  for (const file of filesArray) {
    if (!file.type.includes("video")) continue;

    try {
      const duration = await getVideoDuration(file);
      if (duration > 15) {
        result.push(file);
      }
    } catch (err) {
      console.warn(`Failed to get duration for file ${file.name}`, err);
    }
  }

  return result;
};
